import * as Sentry from '@sentry/react';
import {
  monolithTracePropagationTargets,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import { domainMeta } from './domainMeta';

/**
 * Browser error reporting, tracing, profiling and logs for the applicant
 * portal.
 *
 * No session replay. The page URL here is the applicant's credential, and a
 * replay records it where nothing can scrub it: the rrweb meta event that opens
 * every snapshot carries `window.location.href` and reaches no SDK hook, and
 * the DOM snapshot keeps every portal link's `href`. Errors and traces still
 * say what broke, and `applicant.ref` says whose session it was.
 *
 * Dormant unless SENTRY_DSN is baked into the build. That gate is the whole
 * switch: a local `npm run start`, a Storybook build and a Jest run all leave it
 * unset and therefore start no client at all.
 *
 * One bundle serves every tenant, so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load.
 */

export function initSentry(): void {
  const dsn = process.env.SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name.
    release: process.env.COMMIT_SHA,
    tracesSampleRate: sampleRate(process.env.SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(process.env.SENTRY_PROFILES_SAMPLE_RATE, 1),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: process.env.SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Fed by `utils/sentryUser`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // These pages carry candidate PII. Nothing about a candidate reaches Sentry
    // that this app does not put there deliberately.
    sendDefaultPii: false,
    // Transcend's consent scripts fetch from their own hosts, and a blocked or
    // cancelled request rejects inside their code. The SDK appends the failed
    // host to the message, and each release groups the frames anew, so these
    // fill the project with copies of one cause this app cannot fix.
    ignoreErrors: [/\((?:transcend-cdn\.com|telemetry\.transcend\.io)\)$/],
  });

  Sentry.setTag('tenant', domainMeta.tenant);
}
